import React, { useCallback, useEffect, useMemo } from 'react';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { useResizingHeight } from '@atlaskit/motion';
import { Flex, xcss } from '@atlaskit/primitives';
import { ReactRenderer } from '@atlaskit/renderer';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { ConvoAIResponseState } from '@atlassian/ai-agent-streaming';

import { encodeAdf } from '@atlassian/conversation-assistant-content-renderer';
import AIContainer from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-container/index.tsx';
import { useIsAiEnabledForIssue } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useSmartSummarizeCommentsAnalytics } from '@atlassian/jira-issue-smart-request-summary-state/src/common/analytics/index.tsx';
import {
	useActions,
	useIsGenerating,
	useIsPanelShown,
} from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';
import {
	useSmartSummaryStreaming,
	type StreamingError,
} from '@atlassian/jira-issue-smart-request-summary-state/src/services/convo-ai-streaming/index-streaming.tsx';
import {
	onLinkClick,
	smartLinksDefault,
} from '@atlassian/jira-linking-platform-utils/src/index.tsx';
import { useIsPremium } from '@atlassian/jira-platform-react-hooks-use-ai-opt-in/src/index.tsx';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProfileCardProvider } from '@atlassian/jira-profilecard-provider/src/main.tsx';
import { FooterStreaming } from './footer/index.tsx';
import { GenerationSkeleton } from './footer/skeleton/index.tsx';
import { Header } from './header/index.tsx';
import { Container, ContentWrapper } from './styled.tsx';
import { mapMessageTemplateErrorToAdfError } from './utils.tsx';

export const SummaryNonProactive = () => {
	const isAiOptInEnabled = useIsAiEnabledForIssue();
	const isPremium = useIsPremium();
	const isFeatureEnabled = isPremium && isAiOptInEnabled;
	const [isPanelShown] = useIsPanelShown();
	const { closeSummaryPanel, stopLoading, setIsGenerating } = useActions();
	const [isGenerating] = useIsGenerating();
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();
	const dataProviders = new ProviderFactory();
	const profileCardProvider = useProfileCardProvider();
	dataProviders.setProvider('profilecardProvider', profileCardProvider);

	const [{ content, responseState, error }] = useSmartSummaryStreaming();

	const onContainerRef = useCallback((element: HTMLDivElement | null) => {
		if (element && typeof element.scrollIntoView === 'function') {
			element.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	}, []);

	const onClosePanel = useCallback(() => {
		closeSummaryPanel();
		/** - isGenerating refers to when we call stream endpoint before any events come back *
		 *  - initialization refers to initial setup of streaming
		 *  - answer part means still streaming response
		 * */
		const isStreaming =
			isGenerating ||
			responseState === ConvoAIResponseState.AnswerPart ||
			responseState === ConvoAIResponseState.Initialization;
		if (isStreaming) {
			stopLoading();
			fireTrack('aiInteraction dismissed', 'cancelJiraSmartSummarizeComments', {
				isGenerating: isStreaming,
			});
		}
	}, [closeSummaryPanel, isGenerating, responseState, stopLoading, fireTrack]);

	const resizingHeightProps = useResizingHeight({
		duration: () => 1500,
	});

	const adfNode = useMemo(() => {
		if (!content) {
			return null;
		}
		return encodeAdf(content, true);
	}, [content]);

	useEffect(() => {
		/**
		 * isGenerating refers to fetch stream call until we get some sort of content
		 * 	that is not initialisation (initialisation is basically no content). This means we actually have content of
		 * 	either streamed responses (answer part), final response (cached summaries)
		 * 	or error.
		 */
		if (
			isGenerating &&
			(responseState === ConvoAIResponseState.AnswerPart ||
				responseState === ConvoAIResponseState.FinalResponse ||
				responseState === ConvoAIResponseState.Error)
		) {
			setIsGenerating(false);
		}
	}, [isGenerating, responseState, setIsGenerating]);

	if (!isFeatureEnabled || !isPanelShown) {
		return null;
	}

	const renderContent = () => {
		if (!adfNode) {
			return null;
		}

		return (
			<ReactRenderer
				eventHandlers={{
					link: { onClick: onLinkClick },
				}}
				document={adfNode}
				dataProviders={dataProviders}
				smartLinks={smartLinksDefault}
			/>
		);
	};

	const renderError = (errorMessageTemplate: StreamingError['message_template']) => {
		return (
			<ReactRenderer
				eventHandlers={{
					link: { onClick: onLinkClick },
				}}
				document={mapMessageTemplateErrorToAdfError(errorMessageTemplate)}
				dataProviders={dataProviders}
				smartLinks={smartLinksDefault}
			/>
		);
	};

	const renderSummary = () => {
		switch (responseState) {
			case ConvoAIResponseState.Error:
				return renderError(error?.message.message_template);
			case ConvoAIResponseState.AnswerPart:
			case ConvoAIResponseState.FinalResponse:
				return renderContent();
			default:
				return null;
		}
	};

	const renderSummaryComponent = () => {
		if (isGenerating) {
			return (
				<>
					<UFOLoadHold name="ai-smart-summaries.ui.first-stream-loading" />
					<Flex xcss={containerStyle}>
						<GenerationSkeleton />
					</Flex>
				</>
			);
		}

		return (
			<>
				<ContentWrapper {...resizingHeightProps} hasContent>
					{renderSummary()}
				</ContentWrapper>
				{responseState === ConvoAIResponseState.Error ||
				responseState === ConvoAIResponseState.FinalResponse ? (
					<FooterStreaming />
				) : null}
			</>
		);
	};

	return (
		<Container ref={onContainerRef}>
			<AIContainer
				isLoading={isGenerating}
				shouldFitContainer
				spacing="none"
				testId="issue-smart-request-summary.ui.ai-container"
				hasNewBorderExperience
			>
				<Header onClose={onClosePanel} />
				{renderSummaryComponent()}
			</AIContainer>
			<FireScreenAnalytics />
		</Container>
	);
};

const containerStyle = xcss({
	marginBlockStart: 'space.150',
});
